import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { PrimaryButton, SecondaryButton } from "@JohnLewisPartnership/jl-design-system/dist/elements/button/Button";
import { TickOutlined as TickIcon } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickOutlined";
import ContactMethods from "./contact-methods/ContactMethods";
import SlideIn from "../slide-in/SlideIn";
import { getSolutionContactDetails } from "../../utils/data/getContactDetails";
import { dispatchNewEvent } from "../../utils/analytics/trigger/analyticsTrigger";
import { trackMonetateEvent } from "../../context/MonetateContext";

import styles from "./contact-details-box.module.scss";

function TertiaryButton({ children, className, ...props }) {
  return (
    <button type="button" className={classnames(styles["contact-details-box__button--tertiary"], className)} {...props}>
      {children}
    </button>
  );
}

TertiaryButton.propTypes = {
  children: PropTypes.element.isRequired,
  className: PropTypes.string.isRequired,
};

const getButton = buttonType => {
  switch (buttonType) {
    case "tertiary":
      return TertiaryButton;
    case "secondary":
      return SecondaryButton;
    default:
      return PrimaryButton;
  }
};

const ContactDetailsBox = ({
  newDesign,
  fields,
  buttonType,
  solutionId,
  title,
  leftButtonText,
  rightButtonText,
  className,
  location,
  otherContactDetails,
  viewContactDetails,
  showGreyBackground,
  shouldSortContactOptions,
  showEmailOption,
  showContactDetailsByDefault,
}) => {
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [showFeedbackMessage, setShowFeedbackMessage] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);

  const Button = getButton(buttonType);

  useEffect(() => {
    let isMounted = true;

    const fetchDetails = async () => {
      try {
        const solutionContactDetails = await getSolutionContactDetails(location);
        if (isMounted && solutionContactDetails) {
          setContactDetails(solutionContactDetails);
        }
      } catch {
        if (isMounted) setContactDetails(null);
      }
    };

    fetchDetails();

    return () => {
      isMounted = false;
    };
  }, [location]);

  const handleViewContactDetails = useCallback(() => {
    setShowContactDetails(true);
    setShowFeedbackMessage(false);

    dispatchNewEvent("dataStateChange", {
      action: "contactUsDecisionSolutionDetails",
      type: window?.jlData?.page?.decisionTree?.refinement,
    });

    trackMonetateEvent("contact_us_details_retrieved");
  }, []);

  useEffect(() => {
    if (showContactDetailsByDefault) {
      handleViewContactDetails();
    }
  }, [showContactDetailsByDefault]);

  const handleFeedback = useCallback(() => {
    setShowContactDetails(false);
    setShowFeedbackMessage(true);
  }, []);

  return (
    <div
      className={classnames(newDesign ? styles["contact-details-box--new"] : styles["contact-details-box"], className)}
      data-test="contact-details-box"
    >
      <h2
        className={classnames(styles["contact-details-box__title"], {
          [styles["contact-details-box__title--tertiary"]]: buttonType === "tertiary",
          [styles["contact-details-box__title--showContactDetails"]]: showContactDetails,
        })}
      >
        {title}
      </h2>

      {!showContactDetails && !newDesign && !showContactDetailsByDefault && (
        <Button className={styles["contact-details-box__button"]} onClick={handleViewContactDetails}>
          View contact details
        </Button>
      )}

      {newDesign && (
        <div className={styles["contact-details-box__buttons-container--new"]}>
          <SecondaryButton
            className={classnames(
              styles["contact-details-box__button--new"],
              showContactDetails ? styles["contact-details-box__button--new-active"] : null,
            )}
            onClick={handleViewContactDetails}
          >
            {leftButtonText}
          </SecondaryButton>

          <SecondaryButton
            className={classnames(
              styles["contact-details-box__button--new"],
              showFeedbackMessage ? styles["contact-details-box__button--new-active"] : null,
            )}
            onClick={handleFeedback}
          >
            {rightButtonText}
          </SecondaryButton>
        </div>
      )}

      {showFeedbackMessage && (
        <SlideIn>
          <div className={styles["contact-details-box__feedback-message"]}>
            <TickIcon className={styles["contact-details-box__feedback-icon"]} />
            <h4 className={styles["contact-details-box__feedback-heading"]}>Thank you for your feedback</h4>
            <p>We‘ll use your experience to help us make improvements.</p>
          </div>
        </SlideIn>
      )}

      {showContactDetails && (contactDetails || otherContactDetails) && (
        <SlideIn>
          <ContactMethods
            newDesign={newDesign}
            fields={fields}
            solutionId={solutionId}
            location={location}
            contactDetails={contactDetails || otherContactDetails}
            viewContactDetails={viewContactDetails}
            showGreyBackground={showGreyBackground}
            shouldSortContactOptions={shouldSortContactOptions}
            showEmailOption={showEmailOption}
          />
        </SlideIn>
      )}
    </div>
  );
};

ContactDetailsBox.propTypes = {
  newDesign: PropTypes.bool,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      content: PropTypes.string,
    }),
  ),
  buttonType: PropTypes.string,
  solutionId: PropTypes.string,
  title: PropTypes.string,
  leftButtonText: PropTypes.string,
  rightButtonText: PropTypes.string,
  className: PropTypes.string,
  location: PropTypes.string,
  otherContactDetails: PropTypes.shape({}),
  viewContactDetails: PropTypes.bool,
  showGreyBackground: PropTypes.bool,
  shouldSortContactOptions: PropTypes.bool,
  showEmailOption: PropTypes.bool,
  showContactDetailsByDefault: PropTypes.bool,
};

ContactDetailsBox.defaultProps = {
  newDesign: false,
  fields: [],
  buttonType: null,
  solutionId: null,
  title: "Still need help?",
  leftButtonText: "Yes",
  rightButtonText: "No, that helped",
  className: undefined,
  location: undefined,
  otherContactDetails: null,
  viewContactDetails: false,
  showGreyBackground: false,
  shouldSortContactOptions: true,
  showEmailOption: true,
  showContactDetailsByDefault: false,
};

export default ContactDetailsBox;
